import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/shadcn/avatar';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Batch } from '../../../models/Batch';

interface BatchInfoProps {
  batch: Batch;
}

const formatDate = (date: string | null): string | null => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const BatchInfo = ({ batch }: BatchInfoProps) => {
  const { t } = useTranslation('batchesModule');

  const fields = [
    {
      label: t('detail.info.received_at'),
      value: formatDate(batch.received_at),
    },
    {
      label: t('detail.info.expiry_date'),
      value: formatDate(batch.expiry_date) || t('detail.info.no_expiry'),
      muted: !batch.expiry_date,
    },
    {
      label: t('detail.info.supplier_reference'),
      value: batch.supplier_reference || t('detail.info.no_supplier'),
      muted: !batch.supplier_reference,
    },
    {
      label: t('detail.info.total_stock'),
      value: `${batch.total_stock ?? 0} ${t('detail.info.units')}`,
    },
  ];

  return (
    <section className="rounded-lg border border-card-border p-6 space-y-4">
      <h2 className="text-lg font-semibold">{t('detail.info.title')}</h2>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-complementary-text">{t('detail.info.batch_number')}</p>
            <p className="text-sm font-medium text-text">{batch.batch_number}</p>
          </div>
          <div>
            <p className="text-xs text-complementary-text">{t('detail.info.product')}</p>
            {batch.product ? (
              <div className="flex items-center gap-2 mt-1">
                <Avatar className="w-7 h-7 flex-shrink-0">
                  <AvatarImage src={batch.product.image_url ?? undefined} alt={batch.product.name} />
                  <AvatarFallback className="bg-gray-200 text-gray-700 text-xs font-medium">
                    {batch.product.name?.charAt(0)?.toUpperCase() || 'P'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <Link
                    to={`/products/edit/${batch.product.id}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-500 hover:text-blue-600 truncate"
                  >
                    {batch.product.name}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </Link>
                  <p className="text-xs text-complementary-text truncate">{batch.product.code}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm font-medium text-complementary-text">—</p>
            )}
          </div>
        </div>
        {fields.map((field) => (
          <div key={field.label}>
            <p className="text-xs text-complementary-text">{field.label}</p>
            <p className={`text-sm font-medium ${field.muted ? 'text-complementary-text' : 'text-text'}`}>
              {field.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};
